import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useFormContext, useFormState } from 'react-hook-form'

import Yup from '@/utils/yup'
import { confirmPasswordRule, passwordRules } from '@/utils/passwordSchema'
import Form from '@/components/forms/Form'
import { Columns, Column, Rows } from '@/components/layout'
import InputField from '@/components/forms/InputField'
import PasswordInputField from '@/components/forms/PasswordInputField'
import PasswordRuleChecklist from '@/components/forms/PasswordRuleChecklist'
import PhoneNumberField from '@/components/forms/PhoneNumberField'
import WebAppLink from '@/components/common/WebAppLink'

import styles from './FreeTrialForm.module.scss'

const ConfirmPasswordField = () => {
  const { trigger } = useFormContext()

  return (
    <PasswordInputField
      data-testid="confirm-password-input-field"
      fieldOptions={{ onBlur: () => trigger('confirmPassword') }}
      ignoreTouched={false}
      label="Confirm Password"
      labelProps={{}}
      name="confirmPassword"
    />
  )
}

// The checklist beneath the field already reports each rule the password
// breaks, so the field's own message is kept for the one case the checklist
// cannot show: an empty password.
const PasswordField = (props) => {
  const { errors } = useFormState({ name: 'password' })

  return (
    <PasswordInputField
      shouldDisplayErrorMessage={errors.password?.type === 'required'}
      {...props}
    />
  )
}

const commonInputProps = {
  size: 'large',
}

const NewUser = ({ createOrder, formValues }) => {
  const submitOrder = async ({ confirmPassword, ...model }) => {
    const orderData = { ...formValues, ...model }
    await createOrder(orderData)
  }

  const requirePassword = useMemo(() => (!(formValues.userId || formValues.googleId)), formValues)

  const schema = Yup.object({
    firstName: Yup.string().trim().max(50).required(),
    lastName: Yup.string().trim().max(50).required(),
    phone: Yup.string().trim().phoneNumber({ path: 'phone' }).required(),
    ...(requirePassword && { password: passwordRules(), confirmPassword: confirmPasswordRule() }),
  })

  return (
    <>
      <h2 className={styles.headline}>
        {formValues.userId ? 'Welcome Back!' : 'Sign Up Today'}
      </h2>
      <Form
        initialValues={{ password: '', confirmPassword: '', firstName: formValues.firstName || '', lastName: formValues.lastName || '', phone: '' }}
        onSubmit={submitOrder}
        options={{ criteriaMode: 'all', mode: 'onChange' }}
        schema={schema}
      >
        <Rows gap={0}>
          <Columns gap={8} noMobileGap>
            <Column>
              <InputField
                label="First Name"
                name="firstName"
                {...commonInputProps}
                data-testid="first-name-input-field"
              />
            </Column>
            <Column>
              <InputField
                label="Last Name"
                name="lastName"
                {...commonInputProps}
                data-testid="last-name-input-field"
              />
            </Column>
          </Columns>
          <Column>
            <div>
              <PhoneNumberField
                label="Mobile Number"
                name="phone"
                {...commonInputProps}
                data-testid="mobile-number-input-field"
              />
            </div>
          </Column>
          {requirePassword &&
          <Column gap={0}>
            <PasswordField
              {...commonInputProps}
              containerProps={{ className: 'nui-form-group--no-margin' }}
              labelProps={{}}
            />
            <PasswordRuleChecklist />
            <Rows marginTop={16}>
              <ConfirmPasswordField />
            </Rows>
          </Column>}

          <Form.Action
            className={styles.submit}
            data-testid="finish-cta"
            messageId="pages.freeTrial.form.submit"
            size="large"
            type="submit"
          />
        </Rows>
      </Form>
      <p className="text-center">
        Already have an account?{' '}
        <WebAppLink
          className={styles.noUnderline}
          eventTrackingProperties={{ location: 'Free Trial New User' }}
          messageId="cta.login"
        />
      </p>
    </>
  )
}

NewUser.propTypes = {
  createOrder: PropTypes.func.isRequired,
  formValues: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    userId: PropTypes.string,
  }),
}

export default NewUser
